import type { Block, LegalDoc, Section } from './legalContent'

// A deliberately small text format so legal documents can be edited in a plain text box:
//   ## Heading            starts a section (anything before the first heading is the introduction)
//   - item                bullet list (consecutive lines)
//   1. item               numbered list (consecutive lines)
//   > text                highlighted note
//   | A | B |             table (first row is the header; a |---|---| line under it is optional)
//   plain text            a paragraph (a blank line ends it)

const HEADING = /^##\s+(.*)$/
const BULLET = /^-\s+(.*)$/
const NUMBERED = /^\d+\.\s+(.*)$/
const NOTE = /^>\s?(.*)$/
const TABLE = /^\|.*\|\s*$/
const TABLE_RULE = /^\|[\s:|-]+\|\s*$/

const cells = (line: string) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim())

/** Turns the markup into blocks. Never throws: unrecognised lines simply become paragraphs. */
export function parseLegalMarkup(text: string): { intro: Block[]; sections: Section[] } {
  const intro: Block[] = []
  const sections: Section[] = []
  let target: Block[] = intro

  const lines = text.replace(/\r\n?/g, '\n').split('\n')
  let i = 0
  while (i < lines.length) {
    const line = lines[i].trimEnd()
    const trimmed = line.trim()

    if (!trimmed) { i++; continue }

    const heading = HEADING.exec(trimmed)
    if (heading) {
      const section: Section = { heading: heading[1].trim(), blocks: [] }
      sections.push(section)
      target = section.blocks
      i++
      continue
    }

    if (BULLET.test(trimmed) || NUMBERED.test(trimmed)) {
      const ordered = NUMBERED.test(trimmed)
      const pattern = ordered ? NUMBERED : BULLET
      const items: string[] = []
      while (i < lines.length && pattern.test(lines[i].trim())) {
        items.push(pattern.exec(lines[i].trim())![1].trim())
        i++
      }
      target.push({ type: ordered ? 'ol' : 'ul', items })
      continue
    }

    if (NOTE.test(trimmed)) {
      const parts: string[] = []
      while (i < lines.length && NOTE.test(lines[i].trim())) {
        parts.push(NOTE.exec(lines[i].trim())![1].trim())
        i++
      }
      target.push({ type: 'note', text: parts.join(' ') })
      continue
    }

    if (TABLE.test(trimmed)) {
      const rows: string[][] = []
      while (i < lines.length && TABLE.test(lines[i].trim())) {
        if (!TABLE_RULE.test(lines[i].trim())) rows.push(cells(lines[i]))
        i++
      }
      const [headers, ...body] = rows
      target.push({ type: 'table', headers, rows: body })
      continue
    }

    const parts: string[] = []
    while (i < lines.length) {
      const t = lines[i].trim()
      if (!t || HEADING.test(t) || BULLET.test(t) || NUMBERED.test(t) || NOTE.test(t) || TABLE.test(t)) break
      parts.push(t)
      i++
    }
    target.push({ type: 'p', text: parts.join(' ') })
  }

  return { intro, sections }
}

function blockToMarkup(block: Block): string {
  switch (block.type) {
    case 'p': return block.text
    case 'note': return `> ${block.text}`
    case 'ul': return block.items.map(i => `- ${i}`).join('\n')
    case 'ol': return block.items.map((i, k) => `${k + 1}. ${i}`).join('\n')
    case 'table': return [
      `| ${block.headers.join(' | ')} |`,
      `|${block.headers.map(() => '---').join('|')}|`,
      ...block.rows.map(r => `| ${r.join(' | ')} |`),
    ].join('\n')
  }
}

/** The inverse of parseLegalMarkup, used to load the built-in text into the editor. */
export function docToMarkup(doc: Pick<LegalDoc, 'intro' | 'sections'>): string {
  const parts: string[] = doc.intro.map(blockToMarkup)
  for (const s of doc.sections) {
    parts.push(`## ${s.heading}`)
    parts.push(...s.blocks.map(blockToMarkup))
  }
  return parts.join('\n\n') + '\n'
}
